import { Injectable } from '@angular/core';
import { AbstractControl, AsyncValidatorFn, ValidationErrors, ValidatorFn } from '@angular/forms';
import { core } from 'breeze-client';
import { Observable, of, timer } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { EntityInfo, GlobalValidationContext, ValidationError, ValidationService, ValidatorPredicate } from '.';
import { extractData, isEntity } from '../utils/entity-helper';

@Injectable({ providedIn: 'root' })
export class FormValidators {
  constructor(private validationService: ValidationService) {}

  formControl(
    value: any,
    modelInfoFn: () => EntityInfo,
    propertyName: string,
    group?: string,
    context?: GlobalValidationContext
  ): any {
    return [
      value,
      [this.createSyncValidatorFn(modelInfoFn, propertyName, group, context)],
      [this.createAsyncValidatorFn(modelInfoFn, propertyName, group, context)],
    ];
  }

  createSyncValidatorFn(
    modelInfoFn: () => EntityInfo,
    propertyName: string,
    group?: string,
    context?: GlobalValidationContext
  ): ValidatorFn {
    let lastModel: string;
    let lastValidationErrors: ValidationError[];

    return (control: AbstractControl): ValidationErrors | null => {
      const modelInfo = modelInfoFn();
      const model = {
        ...(isEntity(modelInfo.entity) ? extractData(modelInfo.entity, true, true) : modelInfo.entity),
      };
      model[propertyName] = control.value;
      const modelAsString = JSON.stringify(
        core.toJSONSafe(model, (prop, val) => {
          if (Array.isArray(val)) {
            return val.map((element) => (isEntity(element) ? extractData(element, true, true) : element));
          } else {
            return isEntity(val) ? extractData(val, true, true) : val;
          }
        })
      );

      if (lastModel !== modelAsString) {
        lastModel = modelAsString;
        const errors =
          this.validationService.validateModel(model, modelInfo.entityType, false, {
            predicate: (validator) => (!group && !validator.group) || (group && validator.group === group),
            context,
          }) || [];
        lastValidationErrors = errors.filter((error) => error.context.propertyName === propertyName);
      }

      const validationErrors = lastValidationErrors.reduce((prev, curr) => {
        prev[curr.key] = curr;
        return prev;
      }, {} as ValidationErrors);
      return !!lastValidationErrors.length ? validationErrors : null;
    };
  }

  createAsyncValidatorFn(
    modelInfoFn: () => EntityInfo,
    propertyName: string,
    group?: string,
    context?: GlobalValidationContext
  ): AsyncValidatorFn {
    let lastPropertyValue: any;
    let lastValidationErrors: ValidationError[];

    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      const modelInfo = modelInfoFn();
      const entityMetadata = this.validationService.validationMetadata.getModelConfig(modelInfo.entityType);
      if (!entityMetadata) {
        return of(null);
      }

      const hasAsyncValidators = entityMetadata
        .getAsyncProperties()
        .some((property) =>
          property.validators.some((validator) => (!group && !validator.group) || (group && validator.group === group))
        );
      if (!hasAsyncValidators) {
        return of(null);
      }

      const toErrorMap = (): ValidationErrors =>
        lastValidationErrors.reduce((prev, curr) => {
          prev[curr.key] = curr;
          return prev;
        }, {} as ValidationErrors);

      if (lastPropertyValue !== control.value) {
        const model = {
          ...(isEntity(modelInfo.entity) ? extractData(modelInfo.entity, true, true) : modelInfo.entity),
        };
        model[propertyName] = control.value;

        const predicate: ValidatorPredicate = (validator, property) =>
          property &&
          property.propertyName === propertyName &&
          ((!group && !validator.group) || (group && validator.group === group));
        return timer(500).pipe(
          switchMap(() =>
            this.validationService
              .validateModel(model, modelInfo.entityType, true, {
                predicate,
                context,
              })
              .pipe(
                map((errors) => {
                  lastPropertyValue = control.value;
                  lastValidationErrors = (errors || []).filter(
                    (error) => error && error.context.propertyName === propertyName
                  );

                  return lastValidationErrors.length > 0 ? toErrorMap() : null;
                })
              )
          )
        );
      }

      return of(lastValidationErrors.length > 0 ? toErrorMap() : null);
    };
  }
}
